import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ticketsAPI } from '../lib/api';
import { toast } from 'sonner';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ESTADOS } from '../lib/constants';

const ConsultaTicket = () => {
  const [searchParams] = useSearchParams();
  const [ticketId, setTicketId] = useState(searchParams.get('ticket') || '');
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSearch = async () => {
    if (!ticketId || ticketId.trim() === '') {
      toast.error('Por favor, ingrese un código de ticket');
      return;
    }

    setLoading(true);
    try {
      const response = await ticketsAPI.getById(ticketId);
      setTicket(response.data);
      toast.success('Ticket encontrado');
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error('Ticket no encontrado');
      } else {
        toast.error('Error al buscar el ticket');
      }
      setTicket(null);
    } finally {
      setLoading(false);
    }
  };

  const getEstadoColor = (estado) => {
    const estadoObj = ESTADOS.find(e => e.value === estado);
    return estadoObj ? estadoObj.color : 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="max-w-3xl mx-auto">
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Consulta tu Solicitud</h1>
        <p className="text-gray-600">Ingresa tu código de ticket para ver el estado</p>
      </div>

      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="flex gap-3">
            <div className="flex-1">
              <Input
                value={ticketId}
                onChange={(e) => setTicketId(e.target.value)}
                placeholder="Ej: CAD-000123"
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                data-testid="input-ticket-id"
              />
            </div>
            <Button onClick={handleSearch} disabled={loading} data-testid="btn-search">
              {loading ? 'Buscando...' : 'Buscar'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {ticket && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Ticket {ticket.ticket_id}</CardTitle>
              <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getEstadoColor(ticket.estado)}`}>
                {ticket.estado}
              </span>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              <div>
                <h3 className="font-semibold text-sm text-gray-500 mb-2">Información de Contacto</h3>
                <div className="space-y-1 text-sm">
                  <p><strong>Nombre:</strong> {ticket.nombre_ciudadano}</p>
                  <p><strong>Teléfono:</strong> {ticket.telefono}</p>
                  <p><strong>Email:</strong> {ticket.email}</p>
                </div>
              </div>

              <div>
                <h3 className="font-semibold text-sm text-gray-500 mb-2">Ubicación</h3>
                <div className="space-y-1 text-sm">
                  <p><strong>Dirección:</strong> {ticket.direccion}</p>
                  <p><strong>Lugar:</strong> {ticket.lugar}</p>
                </div>
              </div>

              <div>
                <h3 className="font-semibold text-sm text-gray-500 mb-2">Planificación</h3>
                <div className="space-y-1 text-sm">
                  <p><strong>Fecha preferida:</strong> {new Date(ticket.fecha_preferida_retirada).toLocaleDateString('es-ES')}</p>
                  <p><strong>Fecha de creación:</strong> {new Date(ticket.fecha_creacion).toLocaleDateString('es-ES')}</p>
                </div>
              </div>

              <div>
                <h3 className="font-semibold text-sm text-gray-500 mb-2">Items ({ticket.items?.length || 0})</h3>
                <div className="space-y-2">
                  {ticket.items?.map((item, index) => (
                    <div key={index} className="bg-gray-50 p-3 rounded text-sm">
                      <strong>{index + 1}.</strong> {item.tipo_enser} - {item.elemento}
                      {item.datos_item && <span className="text-gray-600"> ({item.datos_item})</span>}
                    </div>
                  ))}
                </div>
              </div>

              {ticket.observaciones && (
                <div>
                  <h3 className="font-semibold text-sm text-gray-500 mb-2">Observaciones</h3>
                  <p className="text-sm text-gray-700">{ticket.observaciones}</p>
                </div>
              )}

              {ticket.historial_estados && ticket.historial_estados.length > 0 && (
                <div>
                  <h3 className="font-semibold text-sm text-gray-500 mb-2">Historial de Estados</h3>
                  <div className="space-y-2">
                    {ticket.historial_estados.map((hist, index) => (
                      <div key={index} className="flex items-start gap-3 text-sm">
                        <div className="w-2 h-2 mt-1.5 rounded-full bg-primary"></div>
                        <div className="flex-1">
                          <p className="font-medium">{hist.estado}</p>
                          <p className="text-xs text-gray-500">
                            {new Date(hist.timestamp).toLocaleString('es-ES')}
                            {hist.comentario && ` - ${hist.comentario}`}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ConsultaTicket;